import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft } from "lucide-react";

const Section = ({ id, title, children }) => (
  <section id={id} className="mb-9" data-testid={`privacy-section-${id}`}>
    <h2 className="text-lg md:text-lg font-bold text-white mb-3">{title}</h2>
    <div className="text-sm text-slate-300 leading-relaxed space-y-3">{children}</div>
  </section>
);

export default function PrivacyPolicy() {
  const updated = "June 2026";
  return (
    <div className="min-h-screen bg-[#0A0C10]" data-testid="privacy-policy-page">
      <div className="border-b border-[#273042] bg-gradient-to-b from-[#121620] to-[#0A0C10]">
        <div className="px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto py-12">
          <Link to="/" data-testid="privacy-back-home" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-xl bg-[#E11D2A]/15 flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-[#E11D2A]" />
            </span>
            <div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-white">Privacy Policy</h1>
              <p className="text-sm text-slate-400 mt-1">B&B KIGALI 89.7 · Last updated {updated}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto py-10">
        <p className="text-sm text-slate-300 leading-relaxed mb-8">
          This Privacy Policy explains how B&B KIGALI ("B&B KIGALI", "we", "us" or "our") collects, uses,
          shares and protects your personal information when you use our website, mobile apps, live radio,
          live TV, and on-demand streaming services (together, the "Services"). By using the Services you
          agree to the practices described here. If you do not agree, please do not use the Services.
        </p>

        <Section id="info-we-collect" title="1. Information We Collect">
          <p>We collect information you provide directly and information generated automatically when you use the Services:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><b>Account &amp; identity data:</b> your phone number (used for OTP sign-in), and optionally your name, email address and profile picture (for example if you sign in with Google).</li>
            <li><b>Verification data:</b> one-time passcodes (OTPs) sent by SMS and/or WhatsApp to confirm it is you.</li>
            <li><b>Subscription &amp; payment data:</b> the plan you choose, currency, transaction references, payment status and receipts. Card and mobile-money details are processed by our payment providers — we do not store your raw card numbers.</li>
            <li><b>Usage &amp; streaming data:</b> the shows, live channels and radio you play, view counts, watch history, device and browser type, approximate location (from IP), and diagnostic logs.</li>
            <li><b>Communications:</b> messages you send us for support and your notification preferences.</li>
          </ul>
        </Section>

        <Section id="how-we-use" title="2. How We Use Your Information">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Create and secure your account and verify your identity via OTP.</li>
            <li>Provide live radio, live TV and on-demand streaming, and remember what you have watched.</li>
            <li>Process subscriptions and payments, activate your access, and issue PDF receipts.</li>
            <li>Send service messages (for example a receipt, or a "we are live now" alert) to your WhatsApp or by SMS where you are eligible and have not opted out.</li>
            <li>Prevent fraud and abuse, enforce content access rules, and keep the Services reliable and secure.</li>
            <li>Understand which programs are popular so we can improve our content and features.</li>
          </ul>
        </Section>

        <Section id="subscriptions-payments" title="3. Subscriptions &amp; Payments">
          <p>
            When you subscribe, we record your plan, amount, currency (RWF, USD or EUR) and payment status.
            Payments are handled by third-party processors — including <b>Stripe</b>, <b>PayPal</b> (including
            debit/credit-card guest checkout) and <b>mobile-money</b> providers. These processors handle your
            card or mobile-money details directly under their own privacy policies; B&B KIGALI never receives
            or stores your full card number. After a successful payment we generate a receipt containing your
            name, a receipt reference, the amount, currency, payment method, date and the package purchased,
            which we may send to your WhatsApp and keep in your account and our records for accounting and
            legal purposes.
          </p>
        </Section>

        <Section id="streaming-content" title="4. Streaming Content &amp; Access">
          <p>
            Our live radio, live TV and video-on-demand content is protected and available to eligible
            subscribers. To deliver and protect this content we record playback events and unique view counts,
            and we may restrict or gate streams based on your subscription status. Some live streams are relayed
            through our secure servers so they can play safely over an encrypted (HTTPS) connection. We do not
            expose private raw streaming URLs to third parties. Third-party embedded players (for example
            YouTube) may set their own cookies and are governed by their own privacy policies.
          </p>
        </Section>

        <Section id="sharing" title="5. How We Share Information">
          <p>We do not sell your personal information. We share it only with:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><b>Service providers</b> who work on our behalf — payment processors, SMS/WhatsApp messaging providers, hosting, storage and analytics — under confidentiality obligations.</li>
            <li><b>Legal &amp; safety</b> recipients when required by law, regulation, legal process, or to protect the rights, safety and property of B&B KIGALI, our users or the public.</li>
            <li><b>Business transfers</b> — if we are involved in a merger, acquisition or asset sale, your information may be transferred subject to this Policy.</li>
          </ul>
        </Section>

        <Section id="cookies" title="6. Cookies &amp; Similar Technologies">
          <p>
            We use cookies, local storage and similar technologies to keep you signed in, remember your
            preferences, secure the Services and measure performance. You can control cookies through your
            browser settings, but some features (such as staying logged in) may not work without them.
          </p>
        </Section>

        <Section id="retention" title="7. Data Retention">
          <p>
            We keep your information for as long as your account is active and as needed to provide the Services.
            Payment and receipt records may be retained longer to meet accounting, tax and legal obligations.
            OTP codes are short-lived and are deleted shortly after they expire or are used.
          </p>
        </Section>

        <Section id="your-rights" title="8. Your Rights &amp; Choices">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Access, update or correct your profile information from your account.</li>
            <li>Opt out of non-essential WhatsApp/SMS notifications (for example "live now" alerts).</li>
            <li>Request deletion of your account and associated personal data, subject to records we must keep by law.</li>
            <li>Withdraw consent where processing is based on consent, without affecting prior processing.</li>
          </ul>
          <p>To exercise any of these rights, contact us using the details below.</p>
        </Section>

        <Section id="security" title="9. Security">
          <p>
            We use technical and organisational measures — including encrypted connections, hashed verification
            codes and access controls — to protect your information. No method of transmission or storage is
            100% secure, but we work to protect your data and to respond promptly to any security incident.
          </p>
        </Section>

        <Section id="children" title="10. Children's Privacy">
          <p>
            The Services are not directed to children under 13 (or the minimum age required in your country).
            We do not knowingly collect personal information from children. If you believe a child has provided
            us information, please contact us and we will delete it.
          </p>
        </Section>

        <Section id="international" title="11. International Users">
          <p>
            B&B KIGALI serves listeners in Rwanda and the diaspora worldwide. Your information may be processed
            in countries other than your own, which may have different data-protection laws. Where required, we
            take steps to ensure your information remains protected in line with this Policy.
          </p>
        </Section>

        <Section id="changes" title="12. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. We will post the updated version here with a new
            "Last updated" date. Significant changes may be communicated through the Services.
          </p>
        </Section>

        <Section id="contact" title="13. Contact Us">
          <p>If you have questions about this Privacy Policy or your data, contact us:</p>
          <ul className="list-none space-y-1.5">
            <li><b>B&B KIGALI 89.7</b></li>
            <li>Email: <a href="mailto:support@bbkigali.com" className="text-[#E11D2A] hover:underline">support@bbkigali.com</a></li>
            <li>Phone: <a href="tel:+250798875272" className="text-[#E11D2A] hover:underline">+250 798 875 272</a></li>
            <li>Web: <a href="https://bbkigali.com" className="text-[#E11D2A] hover:underline">bbkigali.com</a></li>
          </ul>
        </Section>

        <div className="mt-10 pt-6 border-t border-[#273042]">
          <Link to="/" data-testid="privacy-footer-home" className="inline-flex items-center gap-2 text-sm text-[#E11D2A] font-semibold hover:underline">
            <ArrowLeft className="w-4 h-4" /> Return to B&B KIGALI
          </Link>
        </div>
      </div>
    </div>
  );
}
